import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { TRANSLATIONS, LANGUAGES } from "@/i18n/translations";

const LangContext = createContext(null);

const STORAGE_KEY = "rh_lang";

function detectInitial() {
  if (typeof window === "undefined") return "de";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && TRANSLATIONS[stored]) return stored;
  // Default to DE for first-time visitors (Austrian/German business).
  // EN/RU are explicitly chosen by the user via the language switcher.
  return "de";
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(detectInitial);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!TRANSLATIONS[code]) return;
    localStorage.setItem(STORAGE_KEY, code);
    setLangState(code);
  }, []);

  const t = useCallback(
    (key, fallback) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS.de;
      const val = dict[key];
      if (val !== undefined) return val;
      // Fallback to German, then to provided fallback or the key itself
      return TRANSLATIONS.de[key] ?? fallback ?? key;
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, languages: LANGUAGES }),
    [lang, setLang, t]
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
